import axios from 'axios';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { withTokenRefresh } from './tokenManager';
import { API_ENDPOINTS } from '../endpoints';

const GLOBAL_ERROR_TOAST_ID = 'global-api-error';
const displayToast = (message, type = 'error') => {
  toast.update(GLOBAL_ERROR_TOAST_ID, {
    render: message,
    type,
    autoClose: 5000,
    toastId: GLOBAL_ERROR_TOAST_ID,
    isLoading: false,
  });
};

const apiClient = axios.create();

const getAuthStorage = () => {
  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.state || {};
  } catch {
    return {};
  }
};

export const resolveInstitutionId = (pathOrSlug = '') => {
  const normalized = (pathOrSlug || window.location.pathname).toLowerCase();
  if (normalized.includes('/amet') || normalized === 'amet') return '2';
  if (normalized.includes('/do') || normalized === 'do') return '3';
  if (normalized.includes('/bhuddhi') || normalized === 'bhuddhi') return '4';
  if (normalized.includes('/enoch') || normalized === 'enoch') return '1';
  return '1';
};

apiClient.interceptors.request.use(
  (config) => {
    const { token, institution } = getAuthStorage();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    let institutionId = institution?.id;

    if (!institutionId || config.url === API_ENDPOINTS.login) {
      institutionId = resolveInstitutionId();
    }

    config.headers['X-INSTITUTION-ID'] = institutionId || '1';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isProcessingInterceptorError = false;
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isProcessingInterceptorError) {
      return Promise.reject(error);
    }

    isProcessingInterceptorError = true;

    try {
      const status = error?.response?.status;

      if (status === 401) {
        try {
          const { refreshToken, setToken, logout } = useAuthStore.getState();
          if (refreshToken) {
            const refreshed = await withTokenRefresh(
              () => Promise.reject(error),
              async () => {
                const resp = await axios.post(API_ENDPOINTS.login, { refreshToken });
                const newToken = resp?.data?.token;
                if (newToken) setToken(newToken);
              }
            );
            return refreshed;
          }
          logout();
        } catch (e) {
          const { logout } = useAuthStore.getState();
          logout();
        }
      }

      let errorMessage = 'An unexpected error occurred.';

      if (error.response) {
        console.error('API Error Response:', error.response);
        errorMessage = error.response.data?.message || `Error: ${error.response.statusText} (Status: ${error.response.status})`;
      } else if (error.request) {
        console.error('API Network Error:', error.request);
        errorMessage = 'Network Error: Could not connect to the server.';
      } else {
        console.error('API Request Setup Error:', error.message);
        errorMessage = `An error occurred while setting up the request: ${error.message}`;
      }
      displayToast(errorMessage, 'error');
    } finally {
      isProcessingInterceptorError = false;
    }

    return Promise.reject(error);
  }
);

export default apiClient;
